<template>
  <div class="phone-number-container bsp-page">
    <div class="phone-number-loaded bsp-page__content" v-if="!loading && phoneNumber && businessProfile">
      <BspPageHeader
        :breadcrumbs="profileBreadcrumbs"
        title="Business profile"
        :description="`Public information associated with ${phoneNumber.display_phone_number}.`"
        eyebrow="WhatsApp presence"
        icon="tabler:building-store"
      >
        <template #actions>
          <AButton
            color="blue"
            prepend-icon="tabler:device-floppy"
            :loading="saving"
            @click="saveBusinessProfile"
          >
            Save profile
          </AButton>
        </template>
        <template #meta>
          <ATag :text="phoneNumber.verified_name" color="brand" type="secondary" />
        </template>
      </BspPageHeader>

      <section class="bsp-business-profile-section" aria-label="Business profile settings">
        <div class="bsp-profile-layout">
          <aside class="bsp-profile-preview">
            <div class="bsp-profile-preview__heading">
              <div>
                <span class="bsp-profile-preview__eyebrow">Preview</span>
                <h2>How customers will see your business</h2>
              </div>
              <ATag text="WhatsApp" color="success" type="secondary" prepend-icon="tabler:brand-whatsapp" />
            </div>

            <div class="bsp-profile-preview__image">
              <img
                v-if="businessProfile.profile_picture_url && !profileImageFailed"
                :src="businessProfile.profile_picture_url"
                :alt="`Photo of ${businessProfile.about || phoneNumber.verified_name}`"
                @error="profileImageFailed = true"
              />
              <AIcon v-else icon="tabler:building-store" color="primary" size="36" with-container />
            </div>

            <div class="bsp-profile-preview__content">
              <h3>{{ businessProfile.about || phoneNumber.verified_name }}</h3>
              <p>{{ businessProfile.description || 'Add a description to introduce the business.' }}</p>
            </div>

            <div class="bsp-profile-preview__phone">
              <AIcon icon="tabler:phone" size="18" />
              <div>
                <span>Contact phone number</span>
                <strong>{{ phoneNumber.display_phone_number }}</strong>
              </div>
            </div>
          </aside>

          <form class="bsp-profile-form" @submit.prevent="saveBusinessProfile">
            <div class="bsp-profile-form__heading">
              <div>
                <h2>Business information</h2>
                <p>Complete the public information that will appear in WhatsApp.</p>
              </div>
              <ATag text="Public" color="blue" type="secondary" prepend-icon="tabler:world" />
            </div>

            <div class="bsp-form-grid">
              <AInput v-model="businessProfile.about" label="Public name" placeholder="Business name" />
              <AInput v-model="businessProfile.email" type="email" label="Contact email" placeholder="contact@company.com" />
            </div>
            <AInput v-model="businessProfile.address" label="Address" placeholder="Business address" />
            <AInput
              v-model="businessProfile.description"
              type="textarea"
              label="Description"
              placeholder="Briefly describe the business"
              :rows="3"
              :max-rows="5"
              show-counter
              :max-length="256"
            />
            <AInput
              v-model="businessProfile.profile_picture_url"
              type="url"
              label="Profile image URL"
              placeholder="https://..."
            />

            <div class="bsp-repeatable-field">
              <div class="bsp-repeatable-field__heading">
                <div>
                  <h3>Websites</h3>
                  <p>You can publish up to two links.</p>
                </div>
                <AButton
                  v-if="businessProfile.websites.length < 2"
                  variant="text"
                  size="small"
                  prepend-icon="tabler:plus"
                  @click="businessProfile.websites.push('')"
                >
                  Add website
                </AButton>
              </div>
              <div class="bsp-inline-field" v-for="(website, websiteIndex) in businessProfile.websites" :key="websiteIndex">
                <AInput
                  v-model="businessProfile.websites[websiteIndex]"
                  type="url"
                  :label="`Website ${websiteIndex + 1}`"
                  placeholder="https://company.com"
                />
                <AButton
                  icon="tabler:trash"
                  color="red"
                  variant="text"
                  size="small"
                  help-text="Delete website"
                  @click="businessProfile.websites.splice(websiteIndex, 1)"
                />
              </div>
            </div>

            <ASelector
              v-model="businessProfile.vertical"
              :items="businessCategories"
              item-title="title"
              item-value="value"
              label="Business category"
            />
            <AInput
              v-model="businessProfile.profile_picture_handle"
              label="New image handle"
              placeholder="Handle provided by Meta"
              help-text="Use this field when the image was previously uploaded to Meta."
            />

          </form>
        </div>
      </section>
    </div>
    <SpinnerLoader v-else/>
  </div>
</template>

<script setup lang="ts">

import { useRouter, useRoute } from 'vue-router'
import AuthService from '../services/AuthService/AuthService'
import { computed, onMounted, ref, watch } from 'vue'
import SpinnerLoader from '../components/loaders/SpinnerLoader.vue'
import { User } from '../services/AuthService/types'
import { Establishment } from '../services/EstablishmentService/types'
import { PhoneNumber, WhatsappBusinessProfile } from '../services/AccountService/types'
// import AccountService from '../services/AccountService/AccountService'
import { POSITION, useToast } from 'vue-toastification'
import EstablishmentService from '@/services/EstablishmentService/EstablishmentService'
import AccountService from '@/services/AccountService/AccountService'
import MetaService from '@/services/MetaService/MetaService'
import BspPageHeader from '@/components/BspPageHeader.vue'
import { AButton, AIcon, AInput, ASelector, ATag } from '@adereso-ai/vue-components'
// import MetaService from '@/services/MetaService/MetaService'

const router = useRouter()
const route = useRoute()
const toast = useToast()

const loading = ref(true)
const saving = ref(false)
const profileImageFailed = ref(false)
const currentUser = ref<User | undefined>(undefined)
const currentEstablishment = ref<Establishment | undefined>(undefined)
// const establishmentName = ref('')
const phoneNumber = ref<PhoneNumber | undefined>(undefined)

const businessProfile = ref<WhatsappBusinessProfile | undefined>(undefined)

watch(
  () => businessProfile.value?.profile_picture_url,
  () => {
    profileImageFailed.value = false
  }
)

const businessCategories = [
  { title: 'Professional services', value: 'PROF_SERVICES' }
]

const profileBreadcrumbs = computed(() => [
  {
    title: 'WhatsApp Accounts',
    icon: 'tabler:brand-whatsapp',
    to: { name: 'whatsapp-business-accounts' }
  },
  {
    title: 'Account',
    to: { name: 'whatsapp-business-account', params: { wabaId: String(route.params.wabaId) } }
  },
  {
    title: phoneNumber.value?.verified_name || 'Phone number',
    to: {
      name: 'phone-number',
      params: {
        wabaId: String(route.params.wabaId),
        phoneNumberId: String(route.params.phoneNumberId)
      }
    }
  },
  { title: 'Business profile', disabled: true }
])

async function saveBusinessProfile () {
  if (businessProfile.value) {
    saving.value = true
    const metaService = await MetaService.getInstance()
    const phoneNumberId = route.params.phoneNumberId as string
    const wabaId = route.params.wabaId as string
    try {
      await metaService.updateBusinessProfile(wabaId, phoneNumberId, businessProfile.value)
      toastSuccess('Business Profile Saved')
    } catch (error) {
      toastError('Error saving Business Profile')
    }
    saving.value = false
  }
}

async function getEstablishment () {
  const establishmentService = await EstablishmentService.getInstance()
  try {
    const establishment = await establishmentService.getCurrentEstablishment()
    currentEstablishment.value = establishment
  } catch (error) {
    // do nothing
  }
}

async function getCurrentUser () {
  const authService = await AuthService.getInstance()
  currentUser.value = authService.currentUser.value
  if (authService.currentUser.value == null) {
    router.push({ name: 'login', query: route.query })
  }
}

async function getphoneNumber (wabaId: string, phoneNumberId: string) {
  try {
    const accountService = await AccountService.getInstance()
    phoneNumber.value = await accountService.getphoneNumber(wabaId, phoneNumberId)
  } catch {
    toastError('Error getting phone number')
  }
}

async function getBusinessProfile (wabaId: string, phoneNumberId: string) {
  try {
    const metaService = await MetaService.getInstance()
    const response = await metaService.getBusinessProfile(wabaId, phoneNumberId)
    businessProfile.value = response.data[0]
  } catch {
    toastError('Error getting phone number')
  }
}

onMounted(async () => {
  await router.isReady()

  await getCurrentUser()

  await getEstablishment()

  const phoneNumberId = route.params.phoneNumberId as string
  const wabaId = route.params.wabaId as string

  if (phoneNumberId && wabaId) {
    await getphoneNumber(wabaId, phoneNumberId)
    await getBusinessProfile(wabaId, phoneNumberId)
  } else {
    router.push({ name: 'home' })
  }

  loading.value = false
})

function toastError (errorMessage: string) {
  try {
    toast.error(String(errorMessage), {
      position: POSITION.BOTTOM_LEFT
    })
  } catch (error) {
    console.log('error on toast:', error)
  }
}

function toastSuccess (successMessage: string) {
  try {
    toast.success(String(successMessage), {
      position: POSITION.BOTTOM_LEFT
    })
  } catch (error) {
    console.log('error on toast:', error)
  }
}

</script>
